/**
 * VM-14b — Wiederkehrende Sollstellungs-Regel anlegen.
 * Extrahiert aus TenancyDetail.jsx.
 */
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { POSTING_CATEGORY_GROUPS } from "@/lib/constants";
import { F } from "./TenancyBits";

export default function RuleDialog({ tenancyId, tenancy, onSaved }) {
  const [form, setForm] = useState({
    category: "rent_cold",
    amount: tenancy?.rent_cold || 0,
    frequency: "monthly",
    start_date: tenancy?.contract_start || new Date().toISOString().slice(0, 10),
    end_date: tenancy?.contract_end || "",
    due_day: tenancy?.due_day || 3,
    note: "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        amount: Number(form.amount) || 0,
        due_day: Number(form.due_day) || 3,
        end_date: form.end_date || null,
      };
      await api.post(`/tenancies/${tenancyId}/rules`, payload);
      toast.success("Regel angelegt");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>Wiederkehrende Sollstellung</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <F label="Position">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="rule-category-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-80">
                {POSTING_CATEGORY_GROUPS.map((g) => (
                  <div key={g.key}>
                    <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                      {g.label}
                    </div>
                    {g.items.map((it) => (
                      <SelectItem key={it.value} value={it.value} className="pl-4" data-testid={`rule-cat-${it.value}`}>
                        {it.label}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Betrag €">
            <Input type="number" step="0.01" required value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="rule-amount" />
          </F>
          <F label="Frequenz">
            <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">monatlich</SelectItem>
                <SelectItem value="quarterly">quartalsweise</SelectItem>
                <SelectItem value="yearly">jährlich</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Fälligkeit (Tag im Monat)">
            <Input type="number" min={1} max={28} value={form.due_day} onChange={(e) => set("due_day", e.target.value)} />
          </F>
          <F label="Start-Datum">
            <Input type="date" required value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="rule-start" />
          </F>
          <F label="End-Datum (optional)">
            <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="rule-end" />
          </F>
          <F label="Notiz" className="col-span-2">
            <Input value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="z. B. Kaltmiete laut Mietvertrag" />
          </F>
        </div>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="rule-save-btn">Anlegen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
